import React, { useState, useEffect } from 'react';
import AdminHeader from './AdminHeader';
import StatsSummary from './StatsSummary';
import ProductTable from './ProductTable';
import ProductFormModal from './ProductFormModal';
import '../../styles/admin/AdminDashboard.css';

const API_URL = 'https://cake-shop-mern.onrender.com/api';

const AdminDashboard = () => {
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalSales: 0,
    lowStockCount: 0,
    totalInvoices: 0
  });
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadProducts(), loadStats()]);
    } catch (err) {
      console.error('Error loading data:', err);
      showNotification('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/product`);
      const data = await response.json();
      setProducts(data);
      
      const lowStock = data.filter(p => p.stockQty < 10).length;
      setStats(prev => ({ ...prev, totalProducts: data.length, lowStockCount: lowStock }));
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/invoice/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(prev => ({
          ...prev,
          totalSales: data.totalRevenue || 0,
          totalInvoices: data.totalInvoices || 0
        }));
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleSaveProduct = async (productData) => {
    try {
      if (editingProduct) {
        const response = await fetch(`${API_URL}/admin/${editingProduct._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });

        if (response.ok) {
          showNotification('Product updated successfully', 'success');
          loadProducts();
          setShowModal(false);
        } else {
          showNotification('Failed to update product', 'error');
        }
      } else {
        const response = await fetch(`${API_URL}/admin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });

        if (response.ok) {
          showNotification('Product added successfully', 'success');
          loadProducts();
          setShowModal(false);
        } else {
          showNotification('Failed to add product', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving product:', err);
      showNotification('Error saving product', 'error');
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/admin/${productId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        showNotification('Product deleted successfully', 'success');
        loadProducts();
      } else {
        showNotification('Failed to delete product', 'error');
      }
    } catch (err) {
      console.error('Error deleting product:', err);
      showNotification('Error deleting product', 'error');
    }
  };

  return (
    <div className="admin-dashboard">
      <AdminHeader title="Product Management" />

      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <i className={`fas ${notification.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
          <span>{notification.message}</span>
        </div>
      )}

      <div className="dashboard-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading data...</p>
          </div>
        ) : (
          <>
            <StatsSummary stats={stats} />
            <ProductTable
              products={products}
              onEdit={handleEditProduct}
              onDelete={handleDeleteProduct}
              onAdd={handleAddProduct}
            />
          </>
        )}
      </div>

      <ProductFormModal
        show={showModal}
        product={editingProduct}
        onSave={handleSaveProduct}
        onCancel={() => setShowModal(false)}
      />
    </div>
  );
};

export default AdminDashboard;